import { useQuery } from "@tanstack/react-query";
import type { LlmRunListQuery } from "@gb/admin-contracts";
import { api, errorMessage } from "../../api/client";

export type RunFilters = Partial<
  Pick<LlmRunListQuery, "tenant" | "touchpoint" | "status">
> & { offset?: number };

export function useRuns(filters: RunFilters) {
  return useQuery({
    queryKey: ["runs", filters],
    queryFn: async () => {
      const res = await api.runs.list({
        query: { limit: 50, offset: filters.offset ?? 0, ...filters },
      });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body;
    },
  });
}

export function useRunStats() {
  return useQuery({
    queryKey: ["runs", "stats"],
    queryFn: async () => {
      const res = await api.runs.stats({ query: {} });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.stats;
    },
  });
}

/**
 * One call, whole. The thread page reads it only when a call is opened
 * (`enabled`), under the same key, so a call opened there and then on its own
 * page is read once.
 *
 * Never stale: a run row is written once and never changed (the run logger
 * only inserts). Without this, each return to the tab would read every
 * opened call again, which after "Open all" on a long answer is every body
 * of every call, uncut.
 */
export function useRun(tenantSlug: string, runId: string, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["runs", tenantSlug, runId],
    enabled: options.enabled ?? true,
    staleTime: Infinity,
    queryFn: async () => {
      const res = await api.runs.get({ params: { tenantSlug, runId } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body;
    },
  });
}
